import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Droplets, Sparkles, Clock, Star, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const CeramicCoating = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Ceramic Coating Redlands CA | Professional Mobile Ceramic Coating';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile ceramic coating in Redlands, Riverside, and San Bernardino. Starting at $699. Hydrophobic protection, enhanced gloss, and long-lasting paint protection.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Ceramic Coating",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Precision Mobile Detailing",
        "telephone": businessInfo.phone,
        "priceRange": "$$"
      },
      "areaServed": [
        {"@type": "City", "name": "Redlands", "addressRegion": "CA"},
        {"@type": "City", "name": "Loma Linda", "addressRegion": "CA"},
        {"@type": "City", "name": "Yucaipa", "addressRegion": "CA"},
        {"@type": "City", "name": "Rancho Cucamonga", "addressRegion": "CA"},
        {"@type": "City", "name": "Riverside", "addressRegion": "CA"},
        {"@type": "City", "name": "San Bernardino", "addressRegion": "CA"}
      ],
      "offers": {
        "@type": "Offer",
        "price": "699",
        "priceCurrency": "USD",
        "description": "Starting price for ceramic coating services"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const benefits = [
    { icon: Droplets, title: 'Hydrophobic Protection', desc: 'Water beads and rolls off, making washing easier and keeping your vehicle cleaner longer.' },
    { icon: Sparkles, title: 'Enhanced Gloss', desc: 'Deep, mirror-like shine that enhances your paint color and gives a showroom finish.' },
    { icon: Shield, title: 'Environmental Protection', desc: 'Guards against UV rays, bird droppings, tree sap, road salt, and environmental contaminants.' },
    { icon: Clock, title: 'Long-Lasting Durability', desc: 'Outlasts traditional waxes and sealants with protection that can last years with proper maintenance.' }
  ];

  const process = [
    'Thorough wash and decontamination to remove all surface dirt and grime',
    'One step paint correction to remove swirls and light imperfections',
    'Surface prep with isopropyl alcohol to ensure proper bonding',
    'Professional ceramic coating application in controlled environment',
    'Curing period to allow coating to bond with paint surface',
    'Final inspection and care instructions provided'
  ];

  const faqs = [
    {
      question: 'What is ceramic coating?',
      answer: 'Ceramic coating is a liquid polymer that chemically bonds with your vehicle paint to create a protective layer. It provides hydrophobic properties, enhanced gloss, and protection from environmental contaminants.'
    },
    {
      question: 'How much does ceramic coating cost?',
      answer: 'Ceramic coating pricing is $699 for sedans, $799 for midsize SUVs/trucks, and $899 for XL SUVs/trucks. All packages include one step paint correction to prepare the surface for optimal coating adhesion.'
    },
    {
      question: 'How long does ceramic coating last?',
      answer: 'With proper maintenance, professional ceramic coating can last 2-5 years or more depending on the product used and how well the vehicle is maintained. We provide care instructions to maximize longevity.'
    },
    {
      question: 'Do you perform paint correction before ceramic coating?',
      answer: 'Yes. All ceramic coating packages include one step paint correction to remove light swirls and imperfections. This ensures the coating bonds properly and delivers maximum gloss and protection.'
    },
    {
      question: 'Is ceramic coating scratch-proof?',
      answer: 'Ceramic coating provides excellent protection but is not scratch-proof or rock-chip proof. It adds a hard protective layer that resists minor scratches and swirl marks better than bare paint, but it cannot prevent damage from impacts or deep scratches.'
    },
    {
      question: 'Can ceramic coating be applied to any vehicle?',
      answer: 'Ceramic coating can be applied to most vehicles. We evaluate paint condition during consultation. Severely damaged or failing clear coat may require correction or repainting before coating application.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Shield className="w-6 h-6" />
              <span className="text-sm font-semibold uppercase tracking-wider">Premium Paint Protection</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Ceramic Coating
            </h1>
            
            <p className="text-xl mb-6 text-gray-300 leading-relaxed">
              Long-lasting paint protection with hydrophobic properties, enhanced gloss, and easier maintenance. 
              Protect your investment with professional ceramic coating.
            </p>

            <div className="inline-block bg-white text-black px-8 py-4 rounded-lg mb-8">
              <p className="text-sm font-semibold uppercase tracking-wider text-gray-600 mb-2">Pricing</p>
              <div className="flex gap-6 items-center justify-center">
                <div className="text-center">
                  <p className="text-3xl font-bold">$699</p>
                  <p className="text-sm text-gray-600">Sedan</p>
                </div>
                <div className="text-gray-400 text-2xl">/</div>
                <div className="text-center">
                  <p className="text-3xl font-bold">$799</p>
                  <p className="text-sm text-gray-600">Midsize SUV/Truck</p>
                </div>
                <div className="text-gray-400 text-2xl">/</div>
                <div className="text-center">
                  <p className="text-3xl font-bold">$899</p>
                  <p className="text-sm text-gray-600">XL SUV/Truck</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-3">Includes one step paint correction</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Get a Ceramic Coating Quote <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>Professional Installation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Ceramic Coating */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">What is Ceramic Coating?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Ceramic coating is a liquid polymer that chemically bonds with your vehicle's paint, creating a permanent 
                protective layer. Unlike traditional waxes and sealants that sit on top of the paint, ceramic coating forms 
                a semi-permanent bond that provides long-lasting protection.
              </p>
              <p>
                The nano-ceramic particles create a hydrophobic (water-repelling) surface that causes water to bead up and 
                roll off, taking dirt and contaminants with it. This not only protects your paint but makes washing and 
                maintaining your vehicle significantly easier.
              </p>
              <p>
                Professional ceramic coating enhances your paint's gloss and depth, giving your vehicle a stunning, 
                mirror-like finish that lasts for years rather than weeks or months.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Benefits of Ceramic Coating</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-6">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Ceramic Coating Process</h2>
            <p className="text-lg text-gray-600 mb-8">
              Professional paint preparation is critical for optimal ceramic coating performance. We take the time to properly 
              prepare your vehicle's surface before application.
            </p>
            
            <div className="space-y-4">
              {process.map((step, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    {idx + 1}
                  </div>
                  <p className="text-gray-700 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Who Should Get Ceramic Coating?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>Ceramic coating is ideal for:</p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span><strong>New vehicle owners</strong> who want to protect their investment from day one</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span><strong>Car enthusiasts</strong> who demand the best appearance and protection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span><strong>Anyone who parks outside</strong> and wants protection from UV rays and environmental damage</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span><strong>Drivers who want easier maintenance</strong> and less frequent washing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span><strong>Luxury and exotic vehicle owners</strong> seeking premium paint protection</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ceramic vs Wax */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Ceramic Coating vs Traditional Wax</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-black">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Ceramic Coating</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Lasts 2-5+ years</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Chemically bonds to paint</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Superior hydrophobic properties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Enhanced gloss and depth</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Easier maintenance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-black flex-shrink-0 mt-1" />
                      <span>Superior UV and chemical resistance</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Traditional Wax</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Lasts 1-3 months</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Sits on top of paint</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Basic water beading</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Moderate shine enhancement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Requires frequent reapplication</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 flex-shrink-0 mt-1">•</span>
                      <span>Limited protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ceramic Coating Maintenance</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While ceramic coating dramatically reduces maintenance compared to unprotected paint, proper care ensures 
                maximum longevity and performance:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Wash regularly using pH-neutral car shampoo and the two-bucket method</li>
                <li>Avoid automatic car washes with harsh brushes</li>
                <li>Use microfiber towels for drying to prevent scratches</li>
                <li>Remove bird droppings, tree sap, and bug splatter promptly</li>
                <li>Consider periodic maintenance treatments to rejuvenate the coating</li>
              </ul>
              <p>
                We provide detailed care instructions after installation and are available to answer any maintenance questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Areas We Serve</h2>
            <p className="text-lg text-gray-600 mb-8">
              Professional ceramic coating services throughout the Inland Empire
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/service-areas/redlands-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Redlands
              </Link>
              <Link to="/service-areas/loma-linda-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Loma Linda
              </Link>
              <Link to="/service-areas/yucaipa-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Yucaipa
              </Link>
              <Link to="/service-areas/rancho-cucamonga-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Rancho Cucamonga
              </Link>
              <Link to="/service-areas/riverside-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Riverside
              </Link>
              <Link to="/service-areas/san-bernardino-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                San Bernardino
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Protect Your Vehicle?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Get a custom ceramic coating quote for your vehicle. Professional installation with long-lasting results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Get a Quote <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CeramicCoating;
